import { Component, type ErrorInfo, type ReactNode } from 'react';

/* App-wide error boundary: a render crash anywhere below used to white-screen the
   whole app (no boundary existed). Now it shows a readable fallback + the error,
   and ships the crash to the remote logger so we can see it server-side. */

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
  info: string;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, info: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ info: info.componentStack ?? '' });
  }

  override render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;
    return (
      <div role="alert" className="pt-[max(16px,env(safe-area-inset-top))] px-4 pb-4 max-w-[680px] mx-auto text-foreground">
        <h1 className="text-xl font-black">Oups — quelque chose a planté 😕</h1>
        <p className="text-sm opacity-[0.85]">
          L&apos;affichage a rencontré une erreur. Recharge la page ; si ça persiste, ce message aide à corriger&nbsp;:
        </p>
        <pre className="whitespace-pre-wrap break-words bg-secondary border border-border rounded-lg p-3 text-xs mt-3">
          {error.message}
          {info ? `\n\n${info.split('\n').slice(0, 8).join('\n')}` : ''}
        </pre>
        <button type="button" onClick={() => window.location.reload()} className="mt-3 min-h-[44px] px-[18px] rounded-full border-none bg-naia-blue text-card font-black cursor-pointer">
          Recharger
        </button>
      </div>
    );
  }
}
